import React, { useState, useEffect, useRef } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  ArchiveBoxIcon,
  TagIcon,
  UsersIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  FolderIcon,
  BellIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  QueueListIcon
} from '@heroicons/react/24/outline'
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  AppstoreOutlined,
  SettingOutlined,
  LogoutOutlined,
  ShopOutlined,
  GiftOutlined,
  PictureOutlined,
  BranchesOutlined,
  UserSwitchOutlined,
  TeamOutlined,
  BellOutlined,
  UserOutlined,
  InboxOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import { Link } from 'react-router-dom'
import OrderCountBadge from '../components/common/OrderCountBadge'
import authService from '../services/authService'
import { User } from '../types/admin'
import { Dropdown, Button, Menu, Space, Avatar, Badge } from 'antd'
import type { MenuProps } from 'antd'
import { routeConfigs } from '../utils/constant'
import { SoundOutlined } from '@ant-design/icons'

interface MenuItem {
  key: string
  icon: React.ReactNode
  label: React.ReactNode
  roles: string[]
  path: string
}

interface AdminLayoutProps {
  onLogout: () => void
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false)
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const location = useLocation()
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Lấy thông tin user từ localStorage khi component mount
  useEffect(() => {
    const user = authService.getCurrentUser()
    setCurrentUser(user)
  }, [])

  // Danh sách menu chính
  const menu = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: HomeIcon },
    { name: 'Đơn hàng', path: '/admin/orders', icon: ShoppingCartIcon },
    { name: 'Sản phẩm', path: '/admin/products', icon: ShoppingBagIcon },
    { name: 'Danh mục', path: '/admin/categories', icon: FolderIcon },
    { name: 'Kho hàng', path: '/admin/inventory', icon: ArchiveBoxIcon },
    { name: 'Khuyến mãi', path: '/admin/promotions', icon: TagIcon },
    { name: 'Quản lý sections', path: '/admin/sections', icon: QueueListIcon },
    { name: 'Người dùng', path: '/admin/users', icon: UsersIcon },
    { name: 'Thông báo', path: '/admin/notifications', icon: BellIcon },
    { name: 'Nhật ký', path: '/admin/logs', icon: ClipboardDocumentListIcon },
    { name: 'Cài đặt', path: '/admin/settings', icon: Cog6ToothIcon }
  ]

  // Menu dropdown cho user
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <div className='px-2 py-1'>
          <div className='text-sm font-medium text-gray-900'>
            {currentUser?.email}
          </div>
          <div className='text-xs text-black'>
            {currentUser?.role === 'admin'
              ? 'Quản trị viên'
              : currentUser?.role === 'manager'
              ? 'Quản lý'
              : currentUser?.role === 'staff'
              ? 'Nhân viên'
              : ''}
          </div>
        </div>
      ),
      disabled: true
    },
    {
      type: 'divider'
    },
    {
      key: 'profile',
      label: 'Thông tin cá nhân',
      icon: <UserOutlined />
    },
    {
      key: 'settings',
      label: 'Cài đặt tài khoản',
      icon: <SettingOutlined />
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      label: 'Đăng xuất',
      icon: <LogoutOutlined />,
      onClick: onLogout
    }
  ]

  // Kiểm tra đường dẫn hiện tại có đang active hay không
  const isActive = (path: string) => {
    if (path === '/admin/dashboard') {
      return location.pathname === path
    }
    return location.pathname.startsWith(path)
  }

  // Toggle sidebar collapse
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }

  function getMenuItems (): MenuItem[] {
    const listItems = [
      {
        key: 'dashboard',
        icon: <DashboardOutlined className='w-5 h-5'/>,
        label: 'Tổng quan',
        roles: routeConfigs.dashboard.roles,
        path: '/admin/dashboard'
      },
      {
        key: 'orders',
        icon: <ShoppingCartOutlined className='w-5 h-5' />,
        label: (
          <div className='flex justify-between items-center w-full'>
            <span className='mr-2'>Đơn hàng</span>
            <OrderCountBadge />
          </div>
        ),
        roles: routeConfigs.orders.roles,
        path: '/admin/orders'
      },
      {
        key: 'products',
        icon: <AppstoreOutlined className='w-5 h-5'/>,
        label: 'Sản phẩm',
        roles: routeConfigs.products.roles,
        path: '/admin/products'
      },
      {
        key: 'collections',
        icon: <InboxOutlined className='w-5 h-5'/>,
        label: 'Bộ sưu tập',
        roles: routeConfigs.collections.roles,
        path: '/admin/collections'
      },
      {
        key: 'articles',
        icon: <FileTextOutlined className='w-5 h-5'/>,
        label: 'Bài viết',
        roles: routeConfigs.articles.roles,
        path: '/admin/articles'
      },
      {
        key: 'categories',
        icon: <FolderIcon className='w-5 h-5' />,
        label: 'Danh mục',
        roles: routeConfigs.categories.roles,
        path: '/admin/categories'
      },
      // {
      //   key: 'promotions',
      //   icon: <TagIcon className='w-5 h-5' />,
      //   label: 'Khuyến mãi',
      //   roles: routeConfigs.promotions.roles,
      //   path: '/admin/promotions'
      // },
      {
        key: 'sections',
        icon: <PictureOutlined className='w-5 h-5'/>,
        label: 'Quản lý trang chủ',
        roles: routeConfigs.sections.roles,
        path: '/admin/sections'
      },
      // {
      //   key: 'branches',
      //   icon: <BranchesOutlined className='w-5 h-5'/>,
      //   label: 'Chi nhánh',
      //   roles: routeConfigs.branches.roles,
      //   path: '/admin/branches'
      // },
      {
        key: 'customers',
        icon: <TeamOutlined className='w-5 h-5'/>,
        label: 'Khách hàng',
        roles: routeConfigs.customers.roles,
        path: '/admin/customers'
      },
      {
        key: 'users',
        icon: <UserSwitchOutlined className='w-5 h-5'/>,
        label: 'Người dùng',
        roles: routeConfigs.users.roles,
        path: '/admin/users'
      },
      // {
      //   key: 'logs',
      //   icon: <ClipboardDocumentListIcon className='w-5 h-5' />,
      //   label: 'Nhật ký',
      //   roles: routeConfigs.logs.roles,
      //   path: '/admin/logs'
      // }
    ]
    if (currentUser?.role) {
      return listItems.filter(el => el.roles.includes(currentUser.role))
    } else {
      return []
    }
  }

  return (
    <div className='flex overflow-hidden flex-col h-screen bg-gray-50'>
      {/* Header - Desktop & Mobile */}
      <header className='bg-white border-b border-gray-200 shadow-sm'>
        <div className='flex justify-between items-center px-4 py-2'>
          {/* Left side - Logo and menu button */}
          <div className='flex items-center'>
            <button
              type='button'
              className='flex justify-center items-center w-10 h-10 text-gray-500 rounded-md md:hidden hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500'
              onClick={() => setSidebarOpen(true)}
            >
              <span className='sr-only'>Mở menu</span>
              <Bars3Icon className='w-6 h-6' aria-hidden='true' />
            </button>
            <div className='flex flex-shrink-0 items-center'>
              <span className='ml-2 text-lg font-semibold text-black'>
                DeSilk Manager
              </span>
            </div>
          </div>

          {/* Right side - User profile, notifications */}
          <div className='flex items-center space-x-4'>
            {/* User profile dropdown */}
            <Dropdown
              menu={{ items: userMenuItems }}
              placement='bottomRight'
              arrow
            >
              <div className='flex items-center cursor-pointer'>
                <Avatar icon={<UserOutlined />} className='black' />
                <span className='hidden ml-2 text-sm font-medium text-gray-700 md:inline'>
                  {currentUser?.email}
                </span>
              </div>
            </Dropdown>
          </div>
        </div>
      </header>

      <div className='flex overflow-hidden flex-1'>
        {/* Mobile sidebar */}
        <div
          className={`fixed inset-0 flex z-40 md:hidden ${
            sidebarOpen ? 'block' : 'hidden'
          }`}
        >
          <div
            className='fixed inset-0 bg-gray-600 bg-opacity-75'
            aria-hidden='true'
            onClick={() => setSidebarOpen(false)}
          />

          <div className='flex relative flex-col flex-1 pt-5 pb-4 w-full max-w-xs bg-white'>
            <div className='absolute top-0 right-0 pt-2 -mr-12'>
              <button
                type='button'
                className='flex justify-center items-center ml-1 w-10 h-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white'
                onClick={() => setSidebarOpen(false)}
              >
                <span className='sr-only'>Đóng thanh bên</span>
                <XMarkIcon className='w-6 h-6 text-white' aria-hidden='true' />
              </button>
            </div>

            <div className='flex flex-shrink-0 items-center px-4'>
              <span className='text-lg font-semibold text-black'>
                DeSilk Manager
              </span>
            </div>

            <div className='overflow-y-auto flex-1 mt-5 h-0'>
              <nav className='px-2 space-y-1'>
                {getMenuItems().map(item => (
                  <NavLink
                    key={item.key}
                    to={item.path}
                    className={({ isActive }) =>
                      `${
                        isActive
                          ? 'bg-green-50 text-black'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      } group flex items-center px-2 py-2 text-base font-medium rounded-md`
                    }
                  >
                    {item.icon}
                    <span className='ml-3'>{item.label}</span>
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>
        </div>

        {/* Desktop sidebar */}
        <div
          className={`hidden md:flex md:flex-shrink-0 transition-all duration-300 ease-in-out ${
            sidebarCollapsed ? 'md:w-16' : 'md:w-64'
          }`}
        >
          <div className='flex flex-col w-full'>
            <div className='flex relative flex-col flex-1 h-0 bg-white border-r border-gray-200'>
              {/* Nút collapse đặt ở vị trí giữa-phải (center-right) */}
              <div className='absolute -right-3 top-1/2 z-10'>
                <button
                  onClick={toggleSidebar}
                  className='p-1.5 rounded-full bg-white shadow-md text-gray-500 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200'
                  title={sidebarCollapsed ? 'Mở rộng menu' : 'Thu gọn menu'}
                >
                  {sidebarCollapsed ? (
                    <ChevronDoubleRightIcon
                      className='w-4 h-4'
                      aria-hidden='true'
                    />
                  ) : (
                    <ChevronDoubleLeftIcon
                      className='w-4 h-4'
                      aria-hidden='true'
                    />
                  )}
                </button>
              </div>
              <div className='flex overflow-y-auto flex-col flex-1 pt-5 pb-4'>
                <div className='flex flex-shrink-0 justify-center items-center px-4 mb-5'>
                  {!sidebarCollapsed ? (
                    <span className='text-lg font-semibold text-black'>
                      DeSilk
                    </span>
                  ) : null}
                </div>

                <nav className='flex-1 px-2 space-y-1'>
                  {/* Menu Items */}
                  {getMenuItems().map(item => (
                    <NavLink
                      key={item.key}
                      to={item.path}
                      className={({ isActive }) =>
                        `${
                          isActive
                            ? 'bg-green-50 text-black'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        } group flex items-center px-2 py-2 text-sm font-medium rounded-md`
                      }
                    >
                      {item.icon}
                      {!sidebarCollapsed && (
                        <span className='ml-3'>{item.label}</span>
                      )}
                    </NavLink>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className='flex overflow-hidden flex-col flex-1 w-0'>
          {/* Page content */}
          <main className='overflow-y-auto relative flex-1 bg-gray-50 focus:outline-none'>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  )
}

export default AdminLayout
